/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Injectable } from '@angular/core';
import { SeahorseTableComponent } from '../table.component';
import { DEFAULT_PAGE_SIZES } from '../models/table.model';

@Injectable()
export class TableAdapter {
  private _table?: SeahorseTableComponent;

  get tableModel() {
    return this._table?.model;
  }

  get tableData() {
    return this._table?.data;
  }

  adapt(table: SeahorseTableComponent) {
    this._table = table;

    this.setUpColumns();
    this.setUpMinimumPageSize();
    this.setUpGroupedBehavior(table);

    return { ...this.tableModel };
  }

  private setUpColumns() {
    if (!this.tableModel?.columns) {
      return;
    }

    this.tableModel.columns = this.tableModel.columns.sort(
      (a, b) => (a.order || 0) - (b.order || 0)
    );
  }

  private setUpMinimumPageSize() {
    if (!this.tableModel) {
      return;
    }

    if (!this.tableData?.items) {
      return;
    }

    let pageSizes = this.tableModel.rowsPerPage || DEFAULT_PAGE_SIZES;

    const pageSizeConfig = this.tableModel.pageSize || 10;

    if (!pageSizes.includes(pageSizeConfig)) {
      pageSizes.unshift(pageSizeConfig);
    }

    pageSizes = pageSizes.filter((size) => size >= pageSizeConfig);

    this.tableModel.rowsPerPage = pageSizes;
  }

  // Grouped table has specific behavior - the data is fetched server-side as paged list, but it is rendered as client-side table
  // Beside config required for that, we want to cofigure some other default states
  private setUpGroupedBehavior(table: SeahorseTableComponent) {
    this.setUpGroupedColumn();
    this.createGroupedByField();
    this.setUpMaximumPageSize();
    this.setUpExpandedRowKeys(table);
  }

  private setUpGroupedColumn() {
    if (!this.tableModel || !this.tableModel.groupBy) {
      this._table!.groupByColumn = undefined;
      return;
    }

    const groupByColumn = this.tableModel.columns.find(
      (col) => col.key === this.tableModel!.groupBy
    );

    this._table!.groupByColumn = groupByColumn;
  }

  // we need to do data transformation to convert group to string,
  // because otherwise it can be Array (e.g. list field type) or something else
  // which will break grouping
  private createGroupedByField() {
    if (!this.tableModel || !this.tableModel.groupBy) {
      return;
    }

    if (!this.tableData?.items) {
      return;
    }

    this.tableData!.items = this.tableData!.items.map((item) => {
      const groupByItem = item[this.tableModel!.groupBy as string];
      const groupByKey = `__group`;

      return {
        ...item,
        [groupByKey]: groupByItem?.toString(),
      };
    });
  }

  private setUpMaximumPageSize() {
    if (!this.tableModel || !this.tableModel.groupBy) {
      return;
    }

    if (!this.tableData?.totalCount) {
      return;
    }

    let pageSizes = this.tableModel.rowsPerPage || DEFAULT_PAGE_SIZES;

    pageSizes = pageSizes.filter((size) => size < this.tableData!.totalCount);

    if (!pageSizes.includes(this.tableData.totalCount)) {
      pageSizes.push(this.tableData.totalCount);
    }

    this.tableModel.rowsPerPage = pageSizes;
  }

  private setUpExpandedRowKeys(table: SeahorseTableComponent) {
    if (
      !this.tableModel ||
      !this.tableModel.groupBy ||
      !this.tableData?.items
    ) {
      table.expandedRowKeys = {};
      return;
    }

    const expandedRowKeys = this.tableData.items.map((item) => item[`__group`]);

    table.expandedRowKeys = Object.fromEntries(
      expandedRowKeys.map((key) => [key, true])
    );
  }
}
